<script setup lang="ts">
import { computed, ref, shallowRef, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import SideDrawer from "@/components/ui/SideDrawer.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import {
  extractIdFromUrn,
  ReportUrnPrefix,
  type CrossValidationReportDto,
  type EstimatorReportDto,
} from "@/dto";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useToastsStore } from "@/stores/toasts";
import CrossValidationReport from "@/views/reports/CrossValidationReport.vue";
import EstimatorReport from "@/views/reports/EstimatorReport.vue";

const { workspacePublicId, projectName } = defineProps<{
  workspacePublicId: string;
  projectName: string;
}>();
defineExpose({ openEstimatorReport, openCrossValidationReport, close });

const api = useApi();
const router = useRouter();
const toasts = useToastsStore();

const drawer = useTemplateRef<InstanceType<typeof SideDrawer>>("drawer");

const isLoading = ref(false);
const estimatorReport = shallowRef<EstimatorReportDto>();
const crossValidationReport = shallowRef<CrossValidationReportDto>();
let crossValidationReportEstimators = new Map<number, EstimatorReportDto>();

async function openEstimatorReport(reportId: number) {
  drawer.value?.open();
  isLoading.value = true;
  const r = await api.fetchEstimatorReport(workspacePublicId, projectName, reportId);
  if (r === null) {
    toasts.addToast("Report not found", "error", { duration: 5 });
  } else {
    estimatorReport.value = r;
    isLoading.value = false;
  }
}

async function openCrossValidationReport(reportId: number) {
  drawer.value?.open();
  isLoading.value = true;
  const r = await api.fetchCrossValidationReport(workspacePublicId, projectName, reportId);
  if (r === null) {
    toasts.addToast("Report not found", "error", { duration: 5 });
  } else {
    // fetch sub reports and filter potentially not found
    const estimatorReports = (
      await Promise.all(
        r.estimators.map((id) => api.fetchEstimatorReport(workspacePublicId, projectName, id))
      )
    ).filter((er) => er !== null);
    crossValidationReportEstimators = new Map(estimatorReports.map((e) => [e.id, e]));
    crossValidationReport.value = r;
    isLoading.value = false;
  }
}

function onClose(byDrawer: boolean) {
  if (!byDrawer) {
    drawer.value?.close();
  }
  isLoading.value = false;
  estimatorReport.value = undefined;
  crossValidationReport.value = undefined;
  crossValidationReportEstimators.clear();
}

function openRelatedEstimatorReport(reportId: number) {
  const route = router.resolve({
    name: RouteNames.EstimatorReportDetail,
    params: { workspacePublicId, projectName, reportId },
  });
  window.open(route.href, "_blank");
}

function openRelatedCrossValidationReport(urn: string) {
  const reportId = extractIdFromUrn(urn);
  const route = router.resolve({
    name: RouteNames.CrossValidationReportDetail,
    params: { workspacePublicId, projectName, reportId },
  });
  window.open(route.href, "_blank");
}

const parentCrossValidationReportUrn = computed(() => {
  if (estimatorReport.value && "parents_urns" in estimatorReport.value) {
    return (estimatorReport.value as EstimatorReportDto).parents_urns.find((urn) =>
      urn.startsWith(ReportUrnPrefix.CrossValidation)
    );
  }
  return undefined;
});
</script>

<template>
  <SideDrawer @close="onClose(true)" ref="drawer" content-width="88dvw">
    <template #title>
      <slot name="title"></slot>
    </template>

    <div class="report-drawer">
      <SkoreSpinner v-if="isLoading" />

      <template v-else-if="estimatorReport !== undefined">
        <ColorBadge v-if="parentCrossValidationReportUrn" class="parent-info" color="blue" size="l">
          <i class="icon icon-info-circle-2" />
          This estimator is related to
          <i class="icon icon-playground" />
          <span class="link" @click="onClose(false)">
            {{ parentCrossValidationReportUrn }}
          </span>
        </ColorBadge>

        <EstimatorReport
          :report="estimatorReport"
          @open-cross-validation-report="openRelatedCrossValidationReport"
        />
      </template>

      <template v-else-if="crossValidationReport !== undefined">
        <CrossValidationReport
          :report="crossValidationReport"
          :estimators="crossValidationReportEstimators"
          @open-estimator="openRelatedEstimatorReport"
        />
      </template>
    </div>
  </SideDrawer>
</template>

<style scoped>
.report-drawer {
  .parent-info {
    width: calc(100% - (var(--numbers-12)));
    margin-bottom: var(--numbers-16);
    text-align: left;

    .icon {
      vertical-align: middle;
    }

    .link {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
